import {
  Flex,
  Box,
  Text,
  Button,
  Image,
  Avatar,
  Stack,
  HStack,
} from '@chakra-ui/react';
import { COLORS as COLORS_TYPE } from '../../constant';
import { SubSectionHeading } from '../../shared/Typography';
import type { ConnectBranding } from '@finch-api/common/dist/internal/connect/validate';
import type { DropzoneRootProps, DropzoneInputProps } from 'react-dropzone';

interface LogoUploadSectionProps {
  logoPreview: string | null;
  branding: {
    data: ConnectBranding;
  };
  getBrandDisplayName: () => string;
  getLogoDropzoneRootProps: (props?: DropzoneRootProps) => DropzoneRootProps;
  getLogoDropzoneInputProps: (props?: DropzoneInputProps) => DropzoneInputProps;
  getAddLogoButtonRootProps: (props?: DropzoneRootProps) => DropzoneRootProps;
  getAddLogoButtonInputProps: (
    props?: DropzoneInputProps,
  ) => DropzoneInputProps;
  handleRemoveLogo: () => void;
  COLORS: typeof COLORS_TYPE;
  logoRemoved?: boolean;
}

export type { LogoUploadSectionProps };

export function LogoUploadSection({
  logoPreview,
  branding,
  getBrandDisplayName,
  getLogoDropzoneRootProps,
  getLogoDropzoneInputProps,
  getAddLogoButtonRootProps,
  getAddLogoButtonInputProps,
  handleRemoveLogo,
  COLORS,
  logoRemoved,
}: LogoUploadSectionProps) {
  return (
    <Stack fontSize="14px" p="24px">
      <Flex width="100%" gap={16} alignItems="flex-start">
        <Box flex={1} minWidth={0}>
          <SubSectionHeading as="h3" fontSize="14px" pb="2">
            Application Logo
          </SubSectionHeading>
          <Text color={COLORS.GRAY.GRAY_600}>
            With branding turned on, this logo shows next to the the Finch logo
            in Finch Connect. <br />
            <br /> Accepted file types are JPG or PNG. Recommended size is 256 x
            256 pixels.
          </Text>
        </Box>
        <Box flex={1} minWidth={0} mt="30px">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div
              {...getLogoDropzoneRootProps()}
              style={{
                width: 80,
                minWidth: 80,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <input {...getLogoDropzoneInputProps()} />
              {logoRemoved ? (
                <div
                  style={{
                    position: 'relative',
                    height: 80,
                    width: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Avatar
                    name={getBrandDisplayName()[0]}
                    size="xl"
                    boxSize="80px"
                    fontSize="36px"
                    bg="#191919"
                    color="#fff"
                    border={`1px solid ${COLORS.GRAY.GRAY_400}`}
                    showBorder={false}
                    src={undefined}
                    icon={undefined}
                    style={{ userSelect: 'none' }}
                  />
                  <Text
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: -20,
                    }}
                    fontSize="xs"
                    color={COLORS.GRAY.GRAY_600}
                    fontWeight={400}
                    textAlign="center"
                  >
                    Default Logo
                  </Text>
                </div>
              ) : logoPreview ? (
                <Image
                  src={logoPreview}
                  alt="Application Logo Preview"
                  boxSize="80px"
                  borderRadius="full"
                  border={`1px solid ${COLORS.GRAY.GRAY_400}`}
                  background="#FAFAFA"
                  objectFit="cover"
                />
              ) : branding.data?.logo ? (
                <Image
                  src={branding.data.logo}
                  alt="Application Logo"
                  boxSize="80px"
                  borderRadius="full"
                  border={`1px solid ${COLORS.GRAY.GRAY_400}`}
                  background="#FAFAFA"
                  objectFit="cover"
                />
              ) : (
                <div
                  style={{
                    position: 'relative',
                    height: 80,
                    width: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Avatar
                    name={getBrandDisplayName()[0]}
                    size="xl"
                    boxSize="80px"
                    fontSize="36px"
                    bg="#191919"
                    color="#fff"
                    border={`1px solid ${COLORS.GRAY.GRAY_400}`}
                    showBorder={false}
                    src={undefined}
                    icon={undefined}
                    style={{ userSelect: 'none' }}
                  />
                  <Text
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: -20,
                    }}
                    fontSize="xs"
                    color={COLORS.GRAY.GRAY_600}
                    fontWeight={400}
                    textAlign="center"
                  >
                    Default Logo
                  </Text>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
              <Button
                {...getAddLogoButtonRootProps()}
                variant="outline"
                style={{ fontWeight: 500 }}
                _hover={{ bg: COLORS.GRAY.GRAY_100 }}
              >
                <input {...getAddLogoButtonInputProps()} />
                Add Logo
              </Button>
              {!logoRemoved && (logoPreview || branding.data?.logo) && (
                <Button
                  onClick={handleRemoveLogo}
                  variant="outline"
                  style={{ fontWeight: 500 }}
                  _hover={{
                    bg: COLORS.LIGHT_RED,
                    color: COLORS.FINCH.ERROR_RED,
                  }}
                >
                  Remove
                </Button>
              )}
            </div>
          </div>
        </Box>
      </Flex>
    </Stack>
  );
}
